import React from 'react'
import { Table, Box, HStack, Text, Badge, IconButton, SimpleGrid } from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiChevronDown, FiChevronRight } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import type { UnionWorksiteAffiliation, Steward } from '../../types/union'
import { formatContractPeriod } from '../shared/InfoField'
import { StewardBadge } from './StewardBadge'
import { StatusMenu } from './StatusMenu'
import { InlineEditTextarea } from '../shared/InlineEditTextarea'
import { InlineContactSelect } from '../shared/InlineContactSelect'
import { ContactSearchSelect } from '../shared/ContactSearchSelect'
import type { ContractEditTarget } from './EditContractModal'

export function UnionStandaloneWorksitesSection({
  filteredWorksites,
  expandedWorksiteIds,
  toggleWorksiteExpand,
  handleStandaloneStatusChange,
  handleStandaloneFieldSave,
  handleWorksiteNotesSave,
  handleRemoveSteward,
  setContractEditTarget,
  setDeleteTarget,
  addingStewardFor,
  setAddingStewardFor,
  handleAddSteward,
}: {
  filteredWorksites: UnionWorksiteAffiliation[]
  expandedWorksiteIds: Set<string>
  toggleWorksiteExpand: (id: string) => void
  handleStandaloneStatusChange: (aw: UnionWorksiteAffiliation, isActive: boolean) => void
  handleStandaloneFieldSave: (aw: UnionWorksiteAffiliation, fieldName: string, value: string | null, name?: string | null) => void
  handleWorksiteNotesSave: (worksiteId: string, fieldName: string, value: string) => void
  handleRemoveSteward: (worksiteId: string, stewardId: string) => void
  setContractEditTarget: (target: ContractEditTarget | null) => void
  setDeleteTarget: (target: { type: 'business' | 'standalone'; id: string; name: string } | null) => void
  addingStewardFor: string | null
  setAddingStewardFor: (worksiteId: string | null) => void
  handleAddSteward: (worksiteId: string, contactId: string | null, name?: string | null, role?: string) => void
}) {
  const navigate = useNavigate()
  return (
    <>
      {filteredWorksites.map((aw: UnionWorksiteAffiliation) => {
        const isExpanded = expandedWorksiteIds.has(aw.worksiteUnionId)
        return (
          <React.Fragment key={aw.worksiteUnionId}>
            <Table.Row
              _hover={{ bg: 'gray.50', cursor: 'pointer' }}
              onClick={() => toggleWorksiteExpand(aw.worksiteUnionId)}
            >
              <Table.Cell>
                <HStack gap={1}>
                  <Box as={isExpanded ? FiChevronDown : FiChevronRight} boxSize={3} color="gray.400" />
                  <Badge bg="info.subtle" color="info.fg" fontSize="xs">Standalone</Badge>
                </HStack>
              </Table.Cell>
              <Table.Cell>
                <Text fontWeight="medium" fontSize="sm">{aw.worksiteName}</Text>
                {aw.city && <Text fontSize="xs" color="gray.500">{aw.city}</Text>}
              </Table.Cell>
              <Table.Cell><Text fontSize="sm">{aw.worksiteType || '---'}</Text></Table.Cell>
              <Table.Cell>
                <HStack gap={1}>
                  <Text fontSize="sm">{formatContractPeriod(aw.contractStartDate, aw.contractEndDate)}</Text>
                  <IconButton
                    aria-label="Edit contract period"
                    size="xs"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation()
                      setContractEditTarget({
                        type: 'standalone',
                        label: aw.worksiteName,
                        startDate: aw.contractStartDate,
                        endDate: aw.contractEndDate,
                        worksiteId: aw.worksiteId,
                      })
                    }}><FiEdit2 /></IconButton>
                </HStack>
              </Table.Cell>
              <Table.Cell>
                <StatusMenu
                  isActive={aw.isActive}
                  onStatusChange={(active) => handleStandaloneStatusChange(aw, active)}
                />
              </Table.Cell>
              <Table.Cell>
                <IconButton
                  aria-label="Delete affiliation"
                  size="xs"
                  variant="ghost"
                  colorPalette="red"
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: 'standalone', id: aw.worksiteId, name: aw.worksiteName }) }}><FiTrash2 /></IconButton>
              </Table.Cell>
            </Table.Row>

            {/* Expanded detail panel for standalone worksite */}
            {isExpanded && (
              <Table.Row key={`${aw.worksiteUnionId}-detail`}>
                <Table.Cell colSpan={6} p={0} borderBottom="2px solid" borderColor="info.subtle">
                  <Box bg="info.subtle" px={6} py={4}>
                    <SimpleGrid columns={{ base: 1, md: 3 }} gap={3} mb={3}>
                      <Box bg="gray.50" p={3} borderRadius="md" minH="60px">
                        <InlineEditTextarea
                          label="Union Rep"
                          value={aw.unionRepName}
                          fieldName="unionRepName"
                          canEdit={true}
                          onSave={(_, value) => handleStandaloneFieldSave(aw, 'unionRepName', value)}
                          rows={1}
                        />
                      </Box>
                      <Box bg="gray.50" p={3} borderRadius="md" minH="60px">
                        <InlineContactSelect
                          label="Employer Contact"
                          contactId={aw.employerContactId || undefined}
                          contactName={aw.employerContactName || undefined}
                          fieldName="employerContactId"
                          canEdit={true}
                          onSave={(_, contactId, name) => handleStandaloneFieldSave(aw, 'employerContactId', contactId, name)}
                          allowFreeText
                        />
                        {aw.employerContactId && (
                          <Text
                            fontSize="xs" color="accent" cursor="pointer" mt={1}
                            _hover={{ textDecoration: 'underline' }}
                            onClick={() => navigate(`/dashboard/contacts/${aw.employerContactId}`)}
                          >
                            View contact →
                          </Text>
                        )}
                      </Box>
                      <Box bg="gray.50" p={3} borderRadius="md" minH="60px">
                        <InlineEditTextarea
                          label="Notes"
                          value={aw.notes}
                          fieldName="notes"
                          canEdit={true}
                          onSave={(_, value) => handleWorksiteNotesSave(aw.worksiteId, 'notes', value)}
                          rows={2}
                        />
                      </Box>
                    </SimpleGrid>
                    {/* Stewards */}
                    <HStack gap={1} flexWrap="wrap">
                      <Text fontSize="xs" fontWeight="semibold" color="gray.500" mr={1}>Stewards:</Text>
                      {aw.stewards && aw.stewards.map((s: Steward) => (
                        <StewardBadge
                          key={s.worksiteStewardId}
                          steward={s}

                          onRemove={() => handleRemoveSteward(aw.worksiteId, s.worksiteStewardId)}
                        />
                      ))}
                      {addingStewardFor === aw.worksiteId ? (
                        <Box w="180px">
                          <ContactSearchSelect
                            onSelect={(contactId, name) => {
                              if (contactId || name) handleAddSteward(aw.worksiteId, contactId, name)
                              else setAddingStewardFor(null)
                            }}
                            placeholder="Search steward..."
                            size="xs"
                            allowFreeText
                          />
                        </Box>
                      ) : (
                        <IconButton
                          aria-label="Add steward"
                          size="xs"
                          variant="ghost"
                          colorPalette="teal"
                          onClick={() => setAddingStewardFor(aw.worksiteId)}><FiPlus /></IconButton>
                      )}
                    </HStack>
                    <Text
                      mt={2} fontSize="xs" color="accent" cursor="pointer"
                      _hover={{ textDecoration: 'underline' }}
                      onClick={() => navigate(`/dashboard/worksites/${aw.worksiteId}`)}
                    >
                      Go to worksite detail →
                    </Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}
